import * as types from './catalogTarifs.types';
import { notificationErr } from 'views/components/UI/notification';
import { catalogTarifsError } from 'utils/errorCodes';
import { ITranslate } from 'interfaces/general.interface';
import {
  addClientTarifAPI,
  createTarifAPI,
  deleteClientTarifAPI,
  deleteProductsTarifAPI,
  deleteTarifAPI,
  downloadTarifAPI,
  getCatalogTarifsAPI,
  updateTarifAPI,
} from 'api/endpoints/catalogTarifs/catalogTarifs.api';
import {
  IcreateTarifAPIRequestBody,
  IgetCatalogTarifsAPIResponseDataItem,
  IupdateTarifAPIRequestBody,
} from 'api/endpoints/catalogTarifs/catalogTarifs.response.interface';
import {
  messageError,
  messageSuccess,
  messageWarning,
} from 'views/components/UI/message';
import { saveAs } from 'file-saver';

export async function getCatalogTarifsAction({
  dispatch,
  tenantId,
  token,
  translate,
}: {
  dispatch: React.Dispatch<any>;
  tenantId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  const getCatalog = await getCatalogTarifs({
    dispatch,
    tenantId,
    token,
    translate,
  });
  if (!getCatalog) {
    notificationErr(catalogTarifsError.getCatalogTarifsAction_001, translate);
    return false;
  }
  return true;
}

export async function selectTarifAction({
  dispatch,
  tarif,
}: {
  dispatch: React.Dispatch<any>;
  tarif: IgetCatalogTarifsAPIResponseDataItem | null;
}): Promise<boolean> {
  dispatch({ type: types.SELECT_TARIF, payload: tarif });
  return true;
}

export async function createTarifAction({
  dispatch,
  tenantId,
  body,
  clients,
  tarifs,
  token,
  translate,
}: {
  dispatch: React.Dispatch<any>;
  tenantId: string;
  body: IcreateTarifAPIRequestBody;
  clients: Array<string>;
  tarifs: IgetCatalogTarifsAPIResponseDataItem[];
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({ type: types.SET_IS_LOADING_CREATE_TARIF, payload: true });
    const createTarifFetch = await createTarifAPI({ body, token });

    if (
      createTarifFetch.response.status === 200 ||
      createTarifFetch.response.status === 201
    ) {
      // First remove clients from other tarifs
      for (const tarif of tarifs) {
        const clientExists = tarif.tarif_clients.filter((client) =>
          clients.includes(client.client.id)
        );
        for (const client of clientExists) {
          await deleteClientTarifAPI({
            clientTarifId: client.id,
            token,
          });
        }
      }
      // Second add clients to the updated tarif
      for (const clientId of clients) {
        await addClientTarifAPI({
          clientId,
          tarifId: createTarifFetch.data.id,
          token,
        });
      }
      // Get tarifs
      const getCatalog = await getCatalogTarifs({
        dispatch,
        tenantId,
        token,
        translate,
      });
      if (!getCatalog) {
        notificationErr(catalogTarifsError.updateTarifAction_003, translate);
        return false;
      }

      messageSuccess(translate('actions_catalog-tarifs_create-tarif_success'));
      return true;
    }
    notificationErr(catalogTarifsError.createTarifAction_002, translate);
    messageError(translate('actions_catalog-tarifs_create-tarif_error'));
    return false;
  } catch (err) {
    messageError(translate('actions_catalog-tarifs_create-tarif_error'));
    notificationErr(catalogTarifsError.createTarifAction_001, translate);
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_CREATE_TARIF, payload: false });
  }
}

export async function updateTarifAction({
  dispatch,
  tenantId,
  body,
  clients,
  tarifs,
  token,
  translate,
}: {
  dispatch: React.Dispatch<any>;
  tenantId: string;
  body: IupdateTarifAPIRequestBody;
  clients: Array<string>;
  tarifs: IgetCatalogTarifsAPIResponseDataItem[];
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({ type: types.SET_IS_LOADING_UPDATE_TARIF, payload: true });
  try {
    const updateTarifFetch = await updateTarifAPI({ body, token });

    if (
      updateTarifFetch.response.status === 200 ||
      updateTarifFetch.response.status === 201
    ) {
      // Update Clients
      if (clients.length === 0) {
        const findTarif = tarifs.find((tarif) => tarif.id === body.filter.id);
        if (findTarif) {
          // If no clients, remove all clients from the tarif
          for (const client of findTarif.tarif_clients) {
            await deleteClientTarifAPI({
              clientTarifId: client.id,
              token,
            });
          }
        }
      } else {
        // First remove clients from other tarifs
        for (const tarif of tarifs) {
          const clientExists = tarif.tarif_clients.filter((client) =>
            clients.includes(client.client.id)
          );
          for (const client of clientExists) {
            await deleteClientTarifAPI({
              clientTarifId: client.id,
              token,
            });
          }
        }
        // Second add clients to the updated tarif
        for (const clientId of clients) {
          await addClientTarifAPI({
            clientId,
            tarifId: body.filter.id,
            token,
          });
        }
      }
      // Get tarifs
      const getCatalog = await getCatalogTarifs({
        dispatch,
        tenantId,
        token,
        translate,
      });
      if (!getCatalog) {
        notificationErr(catalogTarifsError.updateTarifAction_003, translate);
        return false;
      }
      messageSuccess(translate('actions_catalog-tarifs_update-tarif_success'));
      return true;
    }
    notificationErr(catalogTarifsError.updateTarifAction_002, translate);
    messageError(translate('actions_catalog-tarifs_update-tarif_error'));
    return false;
  } catch (err) {
    messageError(translate('actions_catalog-tarifs_update-tarif_error'));
    notificationErr(catalogTarifsError.updateTarifAction_001, translate);
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_UPDATE_TARIF, payload: false });
  }
}

export async function deleteTarifAction({
  dispatch,
  tarifId,
  tarifs,
  token,
  translate,
}: {
  dispatch: React.Dispatch<any>;
  tarifId: string;
  tarifs: IgetCatalogTarifsAPIResponseDataItem[];
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({
      type: types.IS_LOADING_DELETE_TARIF,
      payload: {
        tarifId,
        isLoading: true,
      },
    });
    const findTarif = tarifs.find((tarif) => tarif.id === tarifId);
    if (findTarif) {
      for (const client of findTarif.tarif_clients) {
        await deleteClientTarifAPI({
          clientTarifId: client.id,
          token,
        });
      }
    }

    await deleteProductsTarifAPI({
      tarifId,
      token,
    });

    const deleteTarifFetch = await deleteTarifAPI({ tarifId, token });

    if (deleteTarifFetch.response.status === 200) {
      dispatch({ type: types.DELETE_TARIF, payload: tarifId });
      messageSuccess(translate('actions_catalog-tarifs_delete-tarif_success'));
      return true;
    }
    notificationErr(catalogTarifsError.deleteTarifAction_002, translate);
    messageError(translate('actions_catalog-tarifs_delete-tarif_error'));
    return false;
  } catch (err) {
    messageError(translate('actions_catalog-tarifs_delete-tarif_error'));
    notificationErr(catalogTarifsError.deleteTarifAction_001, translate);
    return false;
  } finally {
    dispatch({
      type: types.IS_LOADING_DELETE_TARIF,
      payload: {
        tarifId: '',
        isLoading: false,
      },
    });
  }
}

export async function downloadTarifAction({
  dispatch,
  tenantId,
  tarifId,
  tarifCode,
  pendingProducts,
  token,
  translate,
}: {
  dispatch: React.Dispatch<any>;
  tenantId: string;
  tarifId: string;
  tarifCode: string;
  pendingProducts: boolean;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({ type: types.IS_LOADING_DOWNLOAD_TARIF, payload: true });
  try {
    const downloadTarifFetch = await downloadTarifAPI({
      tenantId,
      tarifId,
      pendingProducts,
      token,
    });

    if (downloadTarifFetch.response.status === 200) {
      saveAs(
        new Blob([downloadTarifFetch.data], { type: 'text/csv' }),
        `tarif_${tarifCode}.csv`
      );
      messageSuccess(translate('actions_catalog-tarifs_download-tarif_success'));
      return true;
    }
    if (downloadTarifFetch.response.status === 404) {
      messageWarning(translate('actions_catalog-tarifs_download-tarif_warning'));
      return false;
    }

    notificationErr(catalogTarifsError.downloadTarifAction_002, translate);
    messageError(translate('actions_catalog-tarifs_download-tarif_error'));
    return false;
  } catch (err) {
    notificationErr(catalogTarifsError.downloadTarifAction_001, translate);
    messageError(translate('actions_catalog-tarifs_download-tarif_error'));
    return false;
  } finally {
    dispatch({ type: types.IS_LOADING_DOWNLOAD_TARIF, payload: false });
  }
}

/////////////////////////////////////////////////////
/////////////////////////////////////////////////////
export async function getCatalogTarifs({
  dispatch,
  tenantId,
  token,
  translate,
}: {
  dispatch: React.Dispatch<any>;
  tenantId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({ type: types.SET_IS_LOADING_CATALOG_TARIFS, payload: true });
    const catalogFetch = await getCatalogTarifsAPI(tenantId, token);

    if (catalogFetch.response.status === 200) {
      dispatch({
        type: types.SET_CATALOG_TARIFS,
        payload: catalogFetch.data,
      });
      return true;
    }
    if (catalogFetch.response.status === 404) {
      return true;
    }
    translate('actions_catalog-tarifs_get-tarif_error');
    notificationErr(catalogTarifsError.getCatalogTarifsAction_003, translate);
    return false;
  } catch (err) {
    translate('actions_catalog-tarifs_get-tarif_error');
    notificationErr(catalogTarifsError.getCatalogTarifsAction_002, translate);
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_CATALOG_TARIFS, payload: false });
  }
}
